/**
 * useUrlState - URL 驱动的状态管理 Hook
 *
 * 核心原则：URL 作为状态的单一数据源 (Single Source of Truth)
 *
 * 使用场景：
 * 1. 跨页面状态传递（如从 Dashboard 跳转到详情页并传递筛选条件）
 * 2. 页面内状态持久化（刷新页面不丢失筛选条件）
 * 3. 分享链接（同事打开链接能看到相同的页面状态）
 *
 * 设计说明：
 * - 读取：使用 useSearchParams 获取当前 URL 参数
 * - 写入：使用 setSearchParams 更新 URL（会触发 History 记录）
 * - 清理：提供 clearUrlState 方法清除指定参数
 * - 导航：提供 navTo 方法进行带参数的页面跳转
 */

import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useMemo } from "react";

/**
 * 通用 URL 状态管理 Hook
 *
 * @returns {Object} - 包含读写 URL 参数的方法
 */
export function useUrlState() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * 获取单个 URL 参数
     * @param {string} key - 参数名
     * @param {*} defaultValue - 默认值（参数不存在时返回）
     * @returns {string|null} - 参数值
     */
    const getParam = useCallback(
        (key, defaultValue = null) => {
            return searchParams.get(key) ?? defaultValue;
        },
        [searchParams],
    );

    /**
     * 获取数组类型的 URL 参数（逗号分隔）
     * @param {string} key - 参数名
     * @returns {string[]} - 参数值数组
     */
    const getArrayParam = useCallback(
        (key) => {
            const value = searchParams.get(key);
            if (!value) return [];
            return value.split(",").filter(Boolean);
        },
        [searchParams],
    );

    /**
     * 获取所有 URL 参数
     * @returns {Object} - 参数对象
     */
    const getAllParams = useCallback(() => {
        const params = {};
        searchParams.forEach((value, key) => {
            params[key] = value;
        });
        return params;
    }, [searchParams]);

    /**
     * 设置 URL 参数（替换模式，会添加到历史记录）
     * @param {Object} params - 要设置的参数对象
     * @param {Object} options - 选项
     * @param {boolean} options.replace - 是否替换当前历史记录（而非新增）
     * @param {boolean} options.merge - 是否与现有参数合并（默认 true）
     */
    const setParams = useCallback(
        (params, options = {}) => {
            const { replace = false, merge = true } = options;

            const newParams = new URLSearchParams(merge ? searchParams : undefined);

            Object.entries(params).forEach(([key, value]) => {
                if (value === null || value === undefined || value === "") {
                    newParams.delete(key);
                } else if (Array.isArray(value)) {
                    if (value.length > 0) {
                        newParams.set(key, value.join(","));
                    } else {
                        newParams.delete(key);
                    }
                } else {
                    newParams.set(key, String(value));
                }
            });

            setSearchParams(newParams, { replace });
        },
        [searchParams, setSearchParams],
    );

    /**
     * 清除指定的 URL 参数
     * @param {string[]} keys - 要清除的参数名数组
     */
    const clearParams = useCallback(
        (keys) => {
            const newParams = new URLSearchParams(searchParams);
            keys.forEach((key) => newParams.delete(key));
            setSearchParams(newParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    /**
     * 导航到指定页面并携带参数
     * @param {string} path - 目标路径
     * @param {Object} params - 要携带的参数
     * @param {Object} options - 导航选项
     * @param {boolean} options.replace - 是否替换当前历史记录
     */
    const navTo = useCallback(
        (path, params = {}, options = {}) => {
            const { replace = false } = options;
            const searchString = new URLSearchParams(
                Object.entries(params)
                    .filter(([, v]) => v !== null && v !== undefined && v !== "")
                    .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : String(v)]),
            ).toString();

            const url = searchString ? `${path}?${searchString}` : path;
            navigate(url, { replace });
        },
        [navigate],
    );

    /**
     * 检查是否有指定的 action 参数（常用于初始化动作）
     * @param {string} actionName - 要检查的 action 名称
     * @returns {boolean}
     */
    const hasAction = useCallback(
        (actionName) => {
            return getParam("action") === actionName;
        },
        [getParam],
    );

    /**
     * 消费 action 参数（执行后清除，防止重复执行）
     * @param {string} actionName - 要消费的 action 名称
     * @returns {boolean} - 是否成功消费（存在该 action）
     */
    const consumeAction = useCallback(
        (actionName) => {
            if (hasAction(actionName)) {
                clearParams(["action"]);
                return true;
            }
            return false;
        },
        [hasAction, clearParams],
    );

    return {
        // 读取
        getParam,
        getArrayParam,
        getAllParams,
        // 写入
        setParams,
        clearParams,
        // 导航
        navTo,
        // Action 处理
        hasAction,
        consumeAction,
        // 原始对象（高级用法）
        searchParams,
        location,
    };
}

/**
 * 创建带类型提示的导航 Helper（供 Widget 等组件使用）
 *
 * 使用示例：
 * const navHelper = createNavHelper(navigate);
 * navHelper.toFlightMonitor({ registrations: ['B-001'], action: 'open_detail', flight_id: 123 });
 */
export function createNavHelper(navigate) {
    return {
        /**
         * 导航到航班监控页面
         * @param {Object} params
         * @param {string[]} params.registrations - 飞机注册号列表
         * @param {string} params.action - 初始化动作 (e.g., 'open_detail')
         * @param {number|string} params.flight_id - 航班 ID（用于打开详情）
         */
        toFlightMonitor: (params = {}) => {
            const searchString = new URLSearchParams(
                Object.entries(params)
                    .filter(([, v]) => v !== null && v !== undefined && v !== "")
                    .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : String(v)]),
            ).toString();
            const url = searchString ? `/operation/flight-monitor?${searchString}` : "/operation/flight-monitor";
            navigate(url);
        },

        /**
         * 导航到航班计划编辑页面
         * @param {Object} params
         * @param {string} params.id - 计划 ID（用于编辑）
         */
        toFlightScheduleEditor: (params = {}) => {
            const { id } = params;
            const path = id ? `/flightSchedule/editor/${id}` : "/flightSchedule/editor/new";
            navigate(path);
        },

        /**
         * 导航到航班实时轨迹地图页面
         * @param {Object} params
         * @param {string} params.date - 查询日期 (YYYY-MM-DD)，不传则默认今天
         * @param {string} params.aircraft_reg - 飞机注册号（预留，供未来自动高亮使用）
         */
        toFlightMap: (params = {}) => {
            const searchString = new URLSearchParams(
                Object.entries(params)
                    .filter(([, v]) => v !== null && v !== undefined && v !== "")
                    .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : String(v)]),
            ).toString();
            const url = searchString ? `/operation/flight-map?${searchString}` : "/operation/flight-map";
            navigate(url);
        },

        // 可继续扩展其他页面的导航方法...
    };
}

export default useUrlState;
